import dayjs from 'dayjs';
import type { QualityRange } from '@/features/quality/hooks';

type ToolbarResetFilterSlot = {
  value: string[];
  defaultValue?: string[];
};

type ToolbarResetStateInput = {
  showSearch: boolean;
  searchValue: string;
  selectedTags: string[];
  selectedStatuses?: string[];
  selectedProjects?: string[];
  selectedSuites?: string[];
  selectedEntities?: string[];
  selectedItems?: string[];
  filterSlots: ToolbarResetFilterSlot[];
  hasCustomDates: boolean;
  range: QualityRange;
  defaultRange: QualityRange;
};

type ToolbarSummaryStateInput = {
  summaryCount: number | null;
  summaryStartDay: string | null;
  summaryEndDay: string | null;
  summarySingularLabel: string;
  summaryPluralLabel: string;
  summaryRelatedEntityLabel: string | null;
  summaryEmptyText: string | null;
  summaryLoading: boolean;
  isLatestMode: boolean;
};

export type ToolbarSummaryState = {
  text: string | null;
  showSkeleton: boolean;
  showSlot: boolean;
};

const formatComparisonPeriod = (dayCount: number): string => {
  if (dayCount % 7 === 0) {
    const weekCount = dayCount / 7;
    return `${weekCount.toLocaleString()} week`;
  }

  return `${dayCount.toLocaleString()} day`;
};

const formatSummaryDay = (day: string | null | undefined): string | null => {
  if (!day) return null;
  const parsedDay = dayjs(day);
  return parsedDay.isValid() ? parsedDay.format('MMM D, YYYY') : null;
};

const getSummaryDayCount = (
  startDay: string | null | undefined,
  endDay: string | null | undefined,
): number | null => {
  if (!startDay || !endDay) return null;
  const parsedStartDay = dayjs(startDay);
  const parsedEndDay = dayjs(endDay);
  if (!parsedStartDay.isValid() || !parsedEndDay.isValid()) return null;
  return Math.max(parsedEndDay.diff(parsedStartDay, 'day') + 1, 1);
};

const hasCustomFilterSlotValue = (filterSlot: ToolbarResetFilterSlot): boolean => {
  const defaultValue = filterSlot.defaultValue ?? [];
  return (
    filterSlot.value.length !== defaultValue.length ||
    filterSlot.value.some((optionValue) => !defaultValue.includes(optionValue))
  );
};

export const buildToolbarResetState = ({
  showSearch,
  searchValue,
  selectedTags,
  selectedStatuses,
  selectedProjects,
  selectedSuites,
  selectedEntities,
  selectedItems,
  filterSlots,
  hasCustomDates,
  range,
  defaultRange,
}: ToolbarResetStateInput): { hasDirtyToolbarState: boolean } => ({
  hasDirtyToolbarState:
    (showSearch && searchValue.trim().length > 0) ||
    selectedTags.length > 0 ||
    (selectedStatuses?.length ?? 0) > 0 ||
    (selectedProjects?.length ?? 0) > 0 ||
    (selectedSuites?.length ?? 0) > 0 ||
    (selectedEntities?.length ?? 0) > 0 ||
    (selectedItems?.length ?? 0) > 0 ||
    filterSlots.some(hasCustomFilterSlotValue) ||
    hasCustomDates ||
    range !== defaultRange,
});

export const buildToolbarSummaryState = ({
  summaryCount,
  summaryStartDay,
  summaryEndDay,
  summarySingularLabel,
  summaryPluralLabel,
  summaryRelatedEntityLabel,
  summaryEmptyText,
  summaryLoading,
  isLatestMode,
}: ToolbarSummaryStateInput): ToolbarSummaryState => {
  const formattedSummaryStartDay = formatSummaryDay(summaryStartDay);
  const formattedSummaryEndDay = formatSummaryDay(summaryEndDay);
  const summaryDayCount = getSummaryDayCount(summaryStartDay, summaryEndDay);
  const summaryEntityLabel = summaryCount === 1 ? summarySingularLabel : summaryPluralLabel;
  const summaryRelatedEntityText =
    summaryRelatedEntityLabel && summaryRelatedEntityLabel.trim().length > 0
      ? ` for ${summaryRelatedEntityLabel.trim()}`
      : '';
  const summaryComparisonText =
    !isLatestMode && summaryDayCount !== null
      ? ` Comparing against the previous ${formatComparisonPeriod(summaryDayCount)} period.`
      : '';
  const summaryText =
    summaryCount === 0 && summaryEmptyText !== null && summaryEmptyText.trim().length > 0
      ? summaryEmptyText.trim()
      : summaryCount !== null && formattedSummaryStartDay && formattedSummaryEndDay
        ? `Showing ${summaryCount.toLocaleString()} ${summaryEntityLabel}${summaryRelatedEntityText} between ${formattedSummaryStartDay} - ${formattedSummaryEndDay}.${summaryComparisonText}`
        : null;
  const hasEmptySummaryText = summaryCount === 0 && summaryText !== null;
  const summaryDatePending = isLatestMode && summaryCount !== null && summaryText === null;
  const summaryCountPending =
    summaryCount === null && formattedSummaryStartDay !== null && formattedSummaryEndDay !== null;
  const showSkeleton =
    !hasEmptySummaryText && (summaryLoading || summaryDatePending || summaryCountPending);

  return {
    text: summaryText,
    showSkeleton,
    showSlot: showSkeleton || summaryText !== null,
  };
};
